/**
 * Schema Hierarchy Utilities
 *
 * Shared utilities for transforming flat table lists into hierarchical
 * structures for schema-first UI components.
 */

/**
 * Transform flat table list to hierarchical format for HierarchicalDataExplorer
 *
 * @param {Array} tables - Array of table objects or table names
 * @param {Object} connection - Connection object with connection_details
 * @returns {Array} Transformed array with database_name, schema_name, object_name
 */
export const transformTablesForHierarchy = (tables, connection) => {
  if (!tables || !Array.isArray(tables)) return [];

  // Get database and schema from connection details (fallback values)
  const connectionDetails = connection?.connection_details || {};
  const defaultDatabase = connectionDetails.database || 'Unknown Database';
  const defaultSchema = connectionDetails.schema || 'PUBLIC';

  return tables.map(table => {
    // Handle both string and object table formats
    const tableObj = typeof table === 'object' ? table : { name: table };
    const name = tableObj.name || table;
    const schema = tableObj.schema || defaultSchema; // Use table's schema or fallback to connection schema
    const database = tableObj.database || defaultDatabase; // Use table's database or fallback
    const objectType = tableObj.type || tableObj.object_type || 'table';

    return {
      database_name: database,
      schema_name: schema,
      object_name: name,
      full_name: `${database}.${schema}.${name}`,
      object_type: objectType,
      // Preserve all table properties
      ...tableObj
    };
  });
};

/**
 * Get unique schemas from a list of tables
 *
 * @param {Array} tables - Array of table objects
 * @returns {Array} Array of unique schema names
 */
export const getUniqueSchemas = (tables) => {
  if (!tables || !Array.isArray(tables)) return [];

  const schemas = new Set();
  tables.forEach(table => {
    const schema = typeof table === 'object' ? table.schema : null;
    if (schema) schemas.add(schema);
  });

  return Array.from(schemas).sort();
};

/**
 * Filter tables by schema
 *
 * @param {Array} tables - Array of table objects
 * @param {string} schema - Schema name to filter by
 * @returns {Array} Filtered array of tables
 */
export const filterTablesBySchema = (tables, schema) => {
  if (!tables || !Array.isArray(tables)) return [];
  if (!schema) return tables;

  return tables.filter(table => {
    const tableSchema = typeof table === 'object' ? table.schema : null;
    return tableSchema === schema;
  });
};

/**
 * Group tables by schema
 *
 * @param {Array} tables - Array of table objects
 * @returns {Object} Object with schema names as keys, arrays of tables as values
 */
export const groupTablesBySchema = (tables) => {
  if (!tables || !Array.isArray(tables)) return {};

  const grouped = {};
  tables.forEach(table => {
    const schema = (typeof table === 'object' ? table.schema : null) || 'Unknown';
    if (!grouped[schema]) {
      grouped[schema] = [];
    }
    grouped[schema].push(table);
  });

  return grouped;
};

/**
 * Get schema statistics from table list
 *
 * @param {Array} tables - Array of table objects
 * @returns {Object} Statistics object with counts per schema
 */
export const getSchemaStats = (tables) => {
  if (!tables || !Array.isArray(tables)) return {};

  const stats = {};
  tables.forEach(table => {
    const schema = (typeof table === 'object' ? table.schema : null) || 'Unknown';
    if (!stats[schema]) {
      stats[schema] = {
        tableCount: 0,
        totalRows: 0,
        sizeBytes: 0
      };
    }

    stats[schema].tableCount += 1;
    if (table.row_count) stats[schema].totalRows += table.row_count;
    if (table.size_bytes) stats[schema].sizeBytes += table.size_bytes;
  });

  return stats;
};
